import { PortalMessageTypeEnum } from "@/services/api";
import * as React from "react";
import {
	DismissCircleRegular,
	ErrorCircleRegular,
	DismissRegular,
	WarningRegular,
	CheckmarkCircleRegular,
} from "@fluentui/react-icons";
import { Button, ButtonProps, mergeClasses, Text } from "@fluentui/react-components";
import styles from "./MessageBar.module.scss";
export interface IMessageBarProps {
	message: React.ReactNode;
	messageType?: PortalMessageTypeEnum;
	dismissable?: boolean | null;
	onDismiss?: () => void;
	hidden?: boolean | null;
	buttonText?: string | null;
	buttonUrl?: string | null;
	onClick?: () => void;
}

export function MessageBar({
	message,
	messageType,
	dismissable,
	onDismiss,
	buttonText,
	buttonUrl,
	hidden: dismissed,
	onClick,
}: IMessageBarProps) {
	const [hidden, setHidden] = React.useState(false);
	const icon = React.useMemo(() => {
		switch (messageType) {
			case "error":
				return <DismissCircleRegular />;
			case "severeWarning":
				return <WarningRegular />;
			case "success":
				return <CheckmarkCircleRegular />;
			case "info":
			case "warning":
			default:
				return <ErrorCircleRegular />;
		}
	}, [messageType]);

	const buttonAppearance = React.useMemo(() => {
		switch (messageType) {
			case "error":
			case "severeWarning":
				return "secondary";
			case "success":
			case "info":
			case "warning":
			default:
				return "primary";
		}
	}, [messageType]);

	return !hidden && !dismissed ? (
		<div className={mergeClasses(styles.messageBar, styles[messageType ?? "info"])}>
			<div className={styles.icon}>{icon}</div>
			{typeof message === "string" ? (
				<div className={styles.message} dangerouslySetInnerHTML={{ __html: message }} />
			) : (
				<div className={styles.message}>{message}</div>
			)}
			{(!!buttonUrl || !!onClick) && (
				<div className={styles.button}>
					<Button
						appearance={buttonAppearance}
						href={buttonUrl ?? undefined}
						onClick={onClick}
						as={onClick ? "button" : "a"}
						target="_blank">
						{buttonText ?? "Open"}
					</Button>
				</div>
			)}
			{dismissable && (
				<div className={styles.dismiss}>
					<Button
						onClick={() => {
							setHidden(true);
							if (onDismiss) onDismiss();
						}}
						appearance="transparent"
						icon={<DismissRegular />}
					/>
				</div>
			)}
		</div>
	) : (
		<></>
	);
}
